/**
 *  Fetch 데이타
 *
 *  @author Bruce.Cheong   2026-04-28
 *
 */

import { th } from "date-fns/locale";

/** External imports */

/** Internal imports */
import { getAccountData } from "@/Common/ts/commonTools";   // 로그인 계정 정보

const BE_API_ORIGIN = process.env.REACT_APP_API_URL;
const BE_API_VER = "/api/v3/";
const BE_API_BASE = BE_API_ORIGIN + BE_API_VER;




interface it_fetchResponse {
    isSuccess : boolean ,
    data : null | Record<string, any> ,
    httpStatus : {
        code : number ,
        message : string ,
    } ,
    errorMessage : null | string ,
};





/**
 *  Fetch API (local wrapper) 를 이용하여 백엔드와 통신하는 함수
 *
 *  @author Bruce.Cheong   2026-04-28
 *
 *  @param iUrl - API 엔드포인트 URL (베이스 URL 이후 경로)
 *  @param iMethod - HTTP 메서드 ("get", "post", "put", "delete" 등)
 *  @param iQs - (선택사항) 쿼리스트링 객체 (예: { key1: "value1", key2: "value2" })
 *  @param iBody - (선택사항) 요청 본문 데이터 객체 (예: { name: "John", age: 30 })
 *
 *  @returns it_fetchResponse 객체를 반환 (isSuccess, data, httpStatus, errorMessage 포함)
 */
async function f_fetchData(iUrl: string, iMethod: string, iQs?: Record<string, any>, iPost?: Record<string, any>){

    const RTN : it_fetchResponse = {
        isSuccess : false ,
        data : null ,
        httpStatus : {
            code : 0 ,
            message : "" ,
        } ,
        errorMessage : null ,
    };
    const _V = {
        apiUrl      :   "" ,
        qs          :   "" ,
        fetchData   :   null as null | Record<string, any> | [] ,
        fetchHeader :   {} ,
        token       :   getAccountData.token() || "" ,  // 로그인 계정의 토크값
        errorMessage:   "" ,
    };






    // QS 가 있다면 쿼리스트링으로 설정
    if ( iQs ) {
        _V.qs = "?" + new URLSearchParams(iQs).toString();
    }

    // API URL 설정
    _V.apiUrl = BE_API_BASE + iUrl + _V.qs;

    // header 설정
    _V.fetchHeader = {
        "Authorization": `Token ${_V.token}`,
        "Content-Type" : "application/json" ,
    };





    // fetch API 호출
    await fetch(_V.apiUrl, {
        method : iMethod ,
        headers : _V.fetchHeader ,
        body : iPost ? JSON.stringify(iPost) : null ,
    }).then((iResponse) => {

        RTN.httpStatus.code = iResponse.status;
        RTN.httpStatus.message = iResponse.statusText;

        if ( !iResponse.ok ) {
            iResponse.json().then((iData) => {
                _V.errorMessage = iData?.status?.msg || "";
            });
            _V.errorMessage = _V.errorMessage || `HTTP error! status: ${iResponse.status}, message: ${iResponse.statusText}`;

            // HTTP 오류 처리
            throw new Error(_V.errorMessage);
        }

        // 성공적으로 응답을 받았을 때, JSON으로 파싱하여 data에 저장
        return iResponse.json().then((iData) => {
            RTN.data = iData.result || iData.datas || iData.img_url || {};
            RTN.isSuccess = true;
        });

    }).catch( (iError) => {

        RTN.errorMessage = iError.message || "An error occurred" ;

    });





    // 결과
    return RTN;
}

/**
 *  Backend API fetch 함수들
 *
 *  @author Bruce.Cheong   2026-04-28
 *
 */
const fetchBE = {

    /** 데이타 조회 */
    get : {

        /**
        *  내 촬영 일정
        *
        *  @author Bruce.Cheong   2026-04-28
        *
        *  @param iQs - 쿼리스트링 객체
        *  @param iQs.main_type - 촬영 유형 (0: 패키지 촬영, 1: 단일 촬영)
        *  @param iQs.status_type - 일정 상태 (0: 전체, 1:미완료, 2:완료)
        */
        myScheduleList : async function(iQs = {} as {
            main_type   :   0 | 1 ,
            status_type :   0 | 1 | 2 ,
        }){

            let RTN : it_fetchResponse;

            const _V = {
                api : {
                    baseUrl : "customers/progress_schedule" ,
                    method : "get" ,
                } ,
                fetchResponse : null as null | { isSuccess: boolean, data: any | null, error: any | null } ,
            };





            RTN = await f_fetchData(
                _V.api.baseUrl ,
                _V.api.method ,
                iQs ,
            );





            return RTN;
        } ,

        /**
        *  내 알림목록
        *
        *  @author Bruce.Cheong   2026-04-29
        *
        *  @param iQs - 쿼리스트링 객체
        *  @param iQs.main_type - 촬영 유형 (0: 패키지 촬영, 1: 단일 촬영)
        *  @param iQs.status_type - 일정 상태 (0: 전체, 1:미완료, 2:완료)
        */
        myNotiList : async function(){

            let RTN : it_fetchResponse;

            const _V = {
                api : {
                    baseUrl : "oops/alarm/list" ,
                    method : "get" ,
                } ,
                fetchResponse : null as null | { isSuccess: boolean, data: any | null, error: any | null } ,
            };





            RTN = await f_fetchData(
                _V.api.baseUrl ,
                _V.api.method ,
            );





            return RTN;
        } ,

        /**
        *  내 사진 목록
        *
        *  @author Bruce.Cheong   2026-04-29
        *
        *  @param iQs - 쿼리스트링 객체
        *  @param iQs.main_type - 1:촬영사진, 2:후기사진
        *  @param iQs.status    - 1:원본, 2:편집요청, 3:편집완료 (후기사진은 원본/편집 없음)
        */
        myPhotoList : async function(iQs = {} as {
            main_type   ?:  1 | 2 ,
            status      ?:  1 | 2 | 3 ,
        }){

            let RTN : it_fetchResponse;

            const _V = {
                api : {
                    baseUrl : "oops/my-photo/list" ,
                    method : "get" ,
                } ,
                fetchResponse : null as null | { isSuccess: boolean, data: any | null, error: any | null } ,
            };





            RTN = await f_fetchData(
                _V.api.baseUrl ,
                _V.api.method ,
                iQs ,
            );





            return RTN;
        } ,

        /**
        *  내 사진선택 업로드 목록
        *
        *  @author Bruce.Cheong   2026-04-29
        *
        *  @param iQs - 쿼리스트링 객체
        *  @param iQs.search_type - 검색 유형 (0: 전체, 1:사진선택요청, 2:편집중, 3:승인요청, 4:편집완료, 5:출고완료)
        */
        myPhotoSelectUploadList : async function(iQs = {} as {
            search_type :   0 | 1 | 2 | 3 | 4 | 5 ,
        }){

            let RTN : it_fetchResponse;

            const _V = {
                api : {
                    baseUrl : "oops/product-list" ,
                    method : "get" ,
                } ,
                fetchResponse : null as null | { isSuccess: boolean, data: any | null, error: any | null } ,
            };





            RTN = await f_fetchData(
                _V.api.baseUrl ,
                _V.api.method ,
                iQs ,
            );





            return RTN;
        } ,





        /** 문의하기, FAQ 관련 */

        /**
         *  게시물 목록
         *
         *  @author Bruce.Cheong   2026-05-07
         *
         *  @param iQs - 쿼리스트링 객체
         *  @param iQs.category - 문의 분류 (예: "예약/변경", "촬영 준비", "시설 안내", "결제/상품")
         *  @param iQs.status - 문의 상태 (예: "답변완료", "답변대기")
         */
        postList : async function(iQs = {} as {
            board_pk :  number | null ,
            group_id ?: number | null ,
        }) {

            let RTN : it_fetchResponse;

            const _V = {
                api : {
                    baseUrl : "oops/post/list" ,
                    method : "get" ,
                } ,
                fetchResponse : null as null | { isSuccess: boolean, data: any | null, error: any | null } ,
            };





            RTN = await f_fetchData(
                _V.api.baseUrl ,
                _V.api.method ,
                iQs ,
            );





            return RTN;
        } ,
    } ,

    post : {

        /** 문의하기, FAQ 관련 */

        /**
         *  게시물 등록
         *
         *  @author Bruce.Cheong   2026-05-07
         *
         *  @param iPost - 게시물 데이터 객체
         *  @param iPost.board_pk - 게시판 구분 (예: 1: 1:1 문의, 2: FAQ)
         *  @param iPost.group_id - 사용자 그룹 ID (예: 고객 ID)
         *  @param iPost.office_id - 사무실 ID (예: 1: 본사, 2: 지사)
         *  @param iPost.post_title - 게시물 제목
         *  @param iPost.post_contents - 게시물 내용
         */
        postSave : async function(iPost = {} as {
            board_pk : number ,
            title ?: string ,
            contents : string ,
            post_pk ?: number | null ,
        }) {

            let RTN : it_fetchResponse;

            const _V = {
                api : {
                    baseUrl : "oops/post/save" ,
                    method : "post" ,
                } ,
                fetchResponse : null as null | { isSuccess: boolean, data: any | null, error: any | null } ,
                postData : iPost as Record<string, any> ,
            };





            // _postData에 undefined인 속성이 있으면 제거 (백엔드에서 null과 undefined를 구분하는 경우가 있기 때문)
            Object.keys(_V.postData).forEach((key) => {
                if (_V.postData[key] === undefined) {
                    delete _V.postData[key];
                }
            });





            RTN = await f_fetchData(
                _V.api.baseUrl ,
                _V.api.method ,
                undefined ,
                _V.postData ,
            );




            return RTN;
        } ,

    } ,

};


export default fetchBE;